import { useState, useCallback } from 'react';
import { apiClient } from '../api/client';

// ─── Types ───────────────────────────────────────────────────────────────────

export interface UseOperationsRoleEditResult {
  editingRole: string | null;
  editValues: { assigneeName: string; phone: string; status: string };
  saving: boolean;
  saveError: string | null;
  startEdit: (
    roleName: string,
    current: { assigneeName: string | null; phone: string | null; status: string | null } | null,
  ) => void;
  cancelEdit: () => void;
  updateField: (field: 'assigneeName' | 'phone' | 'status', value: string) => void;
  saveEdit: () => Promise<void>;
}

// ─── Default edit values ─────────────────────────────────────────────────────

const EMPTY_EDIT_VALUES = { assigneeName: '', phone: '', status: '' };

// ─── Hook ────────────────────────────────────────────────────────────────────

export function useOperationsRoleEdit(
  eventId: string,
  onSuccess: () => void,
): UseOperationsRoleEditResult {
  const [editingRole, setEditingRole] = useState<string | null>(null);
  const [editValues, setEditValues] = useState<{ assigneeName: string; phone: string; status: string }>(EMPTY_EDIT_VALUES);
  const [saving, setSaving] = useState<boolean>(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const startEdit = useCallback(
    (
      roleName: string,
      current: { assigneeName: string | null; phone: string | null; status: string | null } | null,
    ) => {
      setEditingRole(roleName);
      setSaveError(null);
      setEditValues({
        assigneeName: current?.assigneeName ?? '',
        phone: current?.phone ?? '',
        status: current?.status ?? '',
      });
    },
    [],
  );

  const cancelEdit = useCallback(() => {
    setEditingRole(null);
    setEditValues(EMPTY_EDIT_VALUES);
    setSaveError(null);
  }, []);

  const updateField = useCallback(
    (field: 'assigneeName' | 'phone' | 'status', value: string) => {
      setEditValues((prev) => ({ ...prev, [field]: value }));
    },
    [],
  );

  const saveEdit = useCallback(async () => {
    if (!editingRole) return;

    setSaving(true);
    setSaveError(null);

    try {
      await apiClient(
        `/logistics-center/event/${eventId}/operations-roles/${encodeURIComponent(editingRole)}`,
        {
          method: 'PUT',
          body: {
            assigneeName: editValues.assigneeName,
            phone: editValues.phone,
            status: editValues.status,
          },
        },
      );

      onSuccess();
      setEditingRole(null);
      setEditValues(EMPTY_EDIT_VALUES);
    } catch (err: unknown) {
      let message = 'Failed to save role assignment. Please try again.';
      if (typeof err === 'object' && err !== null && 'message' in err) {
        const apiErr = err as { message?: string };
        if (apiErr.message) {
          message = apiErr.message;
        }
      }
      setSaveError(message);
    } finally {
      setSaving(false);
    }
  }, [editingRole, editValues, eventId, onSuccess]);

  return {
    editingRole,
    editValues,
    saving,
    saveError,
    startEdit,
    cancelEdit,
    updateField,
    saveEdit,
  };
}
